import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  User,
  Mail,
  Lock,
  CheckCircle,
  XCircle,
  Eye,
  EyeOff
} from 'lucide-react'
import { Card, Button, Input } from '../ui'
import { useAuthStore } from '../../stores/authStore'
import { useToast } from '../../contexts/ToastContext'
import { userService } from '../../services/userService'

interface PasswordValidation {
  minLength: boolean
  hasUppercase: boolean
  hasDigit: boolean
  hasSpecial: boolean
}

const ProfileSettings: React.FC = () => {
  const { user } = useAuthStore()
  const { showError, showSuccess } = useToast()
  const [isSavingProfile, setIsSavingProfile] = useState(false)
  const [isSavingPassword, setIsSavingPassword] = useState(false)
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || ''
  })

  // Track original data to detect changes
  const [originalData, setOriginalData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || ''
  })

  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({})

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })

  const [validation, setValidation] = useState<PasswordValidation>({
    minLength: false,
    hasUppercase: false,
    hasDigit: false,
    hasSpecial: false
  })

  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>(
    {}
  )

  // Real-time password validation
  useEffect(() => {
    if (passwordData.newPassword) {
      setValidation({
        minLength: passwordData.newPassword.length >= 8,
        hasUppercase: /[A-Z]/.test(passwordData.newPassword),
        hasDigit: /\d/.test(passwordData.newPassword),
        hasSpecial: /[^a-zA-Z0-9]/.test(passwordData.newPassword)
      })
    } else {
      setValidation({
        minLength: false,
        hasUppercase: false,
        hasDigit: false,
        hasSpecial: false
      })
    }
  }, [passwordData.newPassword])

  // Computed validation values
  const isPasswordValid = Object.values(validation).every((valid) => valid)
  const doPasswordsMatch =
    passwordData.newPassword === passwordData.confirmPassword &&
    passwordData.confirmPassword.length > 0
  const isNewPasswordDifferent =
    passwordData.currentPassword.length > 0 &&
    passwordData.newPassword !== passwordData.currentPassword
  const canSubmitPassword =
    passwordData.currentPassword.length > 0 &&
    isPasswordValid &&
    doPasswordsMatch &&
    isNewPasswordDifferent &&
    !isSavingPassword

  // Check if personal info has changed
  const hasPersonalInfoChanged =
    formData.firstName !== originalData.firstName ||
    formData.lastName !== originalData.lastName

  // Check if form data is valid (no validation errors would occur)
  const isPersonalInfoValid =
    formData.firstName.trim().length >= 1 &&
    formData.firstName.trim().length <= 50 &&
    formData.lastName.trim().length >= 1 &&
    formData.lastName.trim().length <= 50

  const canSubmitPersonalInfo =
    hasPersonalInfoChanged && isPersonalInfoValid && !isSavingProfile

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {}

    // Validate firstName
    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required'
    } else if (formData.firstName.length < 2) {
      errors.firstName = 'First name must be at least 2 characters'
    } else if (formData.firstName.length > 50) {
      errors.firstName = 'First name must be less than 50 characters'
    }

    // Validate lastName
    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required'
    } else if (formData.lastName.length < 2) {
      errors.lastName = 'Last name must be at least 2 characters'
    } else if (formData.lastName.length > 50) {
      errors.lastName = 'Last name must be less than 50 characters'
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    // Clear error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  const handlePasswordChange = (field: string, value: string) => {
    setPasswordData((prev) => ({ ...prev, [field]: value }))
    // Clear error when user starts typing
    if (passwordErrors[field]) {
      setPasswordErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  const handleSaveProfile = async () => {
    // Validate form
    if (!validateForm()) {
      return
    }

    setIsSavingProfile(true)

    try {
      // Call API to update user
      const response = await userService.updateUser(user!.id, {
        firstName: formData.firstName,
        lastName: formData.lastName
      })

      if (response.success) {
        // Update original data to reflect the saved state
        setOriginalData({
          firstName: formData.firstName,
          lastName: formData.lastName
        })
        showSuccess(
          'Profile Updated',
          'Your personal information has been successfully updated.'
        )
      } else {
        showError(
          'Update Failed',
          response.message || 'Failed to update profile'
        )
      }
    } catch (err) {
      showError(
        'Operation Failed',
        err instanceof Error ? err.message : 'Failed to update profile'
      )
    } finally {
      setIsSavingProfile(false)
    }
  }

  const handleSavePassword = async () => {
    // Clear previous errors
    setPasswordErrors({})

    // Validate current password is entered
    if (!passwordData.currentPassword) {
      setPasswordErrors({ currentPassword: 'Current password is required' })
      return
    }

    // Validate new password
    if (!isPasswordValid) {
      setPasswordErrors({ newPassword: 'Password does not meet requirements' })
      return
    }

    // Validate new password is different from current
    if (passwordData.newPassword === passwordData.currentPassword) {
      setPasswordErrors({
        newPassword: 'New password must be different from current password'
      })
      return
    }

    // Validate password match
    if (!doPasswordsMatch) {
      setPasswordErrors({ confirmPassword: 'Passwords do not match' })
      return
    }

    setIsSavingPassword(true)

    try {
      const response = await userService.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      })

      if (response.success) {
        // Clear password fields after successful save
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        })

        showSuccess(
          'Password Updated',
          response.message || 'Your password has been successfully changed.'
        )
      } else {
        // Handle API error with field-specific errors
        if (response.error?.field) {
          setPasswordErrors({
            [response.error.field]: response.error.message
          })
        } else {
          showError(
            'Update Failed',
            response.error?.message || 'Failed to update password'
          )
        }
      }
    } catch (err) {
      showError(
        'Operation Failed',
        err instanceof Error ? err.message : 'Failed to update password'
      )
    } finally {
      setIsSavingPassword(false)
    }
  }

  // Validation indicator component
  const ValidationIndicator: React.FC<{ isValid: boolean; text: string }> = ({
    isValid,
    text
  }) => (
    <div
      className={`flex items-center space-x-1.5 text-xs ${
        isValid ? 'text-green-600' : 'text-gray-500'
      }`}
    >
      {isValid ? (
        <CheckCircle className='h-3.5 w-3.5' />
      ) : (
        <XCircle className='h-3.5 w-3.5' />
      )}
      <span>{text}</span>
    </div>
  )

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      role='tabpanel'
      id='profile-panel'
      aria-labelledby='profile-tab'
      className='flex justify-center'
    >
      <div className='w-full max-w-2xl space-y-5'>
        {/* Personal Information */}
        <Card variant='outlined' padding='lg'>
          <h3 className='text-sm font-semibold text-gray-900 mb-4'>
            Personal Information
          </h3>

          {/* Email Display (Read-only) */}
          <div className='mb-5 pb-5 border-b border-gray-200'>
            <div className='flex items-center gap-2 mb-1.5'>
              <Mail className='h-3.5 w-3.5 text-gray-400' />
              <label className='block text-xs font-medium text-gray-500 uppercase tracking-wide'>
                Email Address
              </label>
              <Lock className='h-3 w-3 text-gray-400' />
            </div>
            <p className='text-sm text-gray-900 font-medium'>{user?.email}</p>
            <p className='mt-1 text-xs text-gray-500'>
              Contact your administrator to change your email address
            </p>
          </div>

          <div className='space-y-4'>
            {/* Name Fields */}
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <Input
                label='First Name *'
                value={formData.firstName}
                onChange={(e) => handleChange('firstName', e.target.value)}
                placeholder='Enter your first name'
                icon={<User className='h-3.5 w-3.5' />}
                error={validationErrors.firstName}
                disabled={isSavingProfile}
              />

              <Input
                label='Last Name *'
                value={formData.lastName}
                onChange={(e) => handleChange('lastName', e.target.value)}
                placeholder='Enter your last name'
                icon={<User className='h-3.5 w-3.5' />}
                error={validationErrors.lastName}
                disabled={isSavingProfile}
              />
            </div>
          </div>

          {/* Save Button */}
          <div className='flex justify-end mt-5'>
            <Button
              variant='primary'
              onClick={handleSaveProfile}
              loading={isSavingProfile}
              disabled={!canSubmitPersonalInfo}
            >
              {isSavingProfile ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </Card>

        {/* Change Password */}
        <Card variant='outlined' padding='lg'>
          <div className='flex items-center mb-4'>
            <Lock className='h-4 w-4 text-avi-primary mr-2' />
            <h3 className='text-sm font-semibold text-gray-900'>
              Change Password
            </h3>
          </div>

          <div className='space-y-4'>
            {/* Current Password */}
            <div>
              <label
                htmlFor='currentPassword'
                className='block text-xs font-medium text-gray-700 mb-1'
              >
                Current Password
              </label>
              <div className='relative'>
                <Input
                  id='currentPassword'
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={passwordData.currentPassword}
                  onChange={(e) =>
                    handlePasswordChange('currentPassword', e.target.value)
                  }
                  onFocus={(e) => e.target.removeAttribute('readonly')}
                  placeholder='Enter your current password'
                  icon={<Lock className='h-3.5 w-3.5' />}
                  iconPosition='left'
                  error={passwordErrors.currentPassword}
                  autoComplete='off'
                  readOnly
                />
                <button
                  type='button'
                  className='absolute inset-y-0 right-0 pr-3 flex items-center z-10'
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? (
                    <EyeOff className='h-4 w-4 text-gray-400 hover:text-gray-600' />
                  ) : (
                    <Eye className='h-4 w-4 text-gray-400 hover:text-gray-600' />
                  )}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div>
              <label
                htmlFor='newPassword'
                className='block text-xs font-medium text-gray-700 mb-1'
              >
                New Password
              </label>
              <div className='relative'>
                <Input
                  id='newPassword'
                  type={showNewPassword ? 'text' : 'password'}
                  value={passwordData.newPassword}
                  onChange={(e) =>
                    handlePasswordChange('newPassword', e.target.value)
                  }
                  onFocus={(e) => e.target.removeAttribute('readonly')}
                  placeholder='Enter your new password'
                  icon={<Lock className='h-3.5 w-3.5' />}
                  iconPosition='left'
                  error={passwordErrors.newPassword}
                  autoComplete='off'
                  readOnly
                />
                <button
                  type='button'
                  className='absolute inset-y-0 right-0 pr-3 flex items-center z-10'
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? (
                    <EyeOff className='h-4 w-4 text-gray-400 hover:text-gray-600' />
                  ) : (
                    <Eye className='h-4 w-4 text-gray-400 hover:text-gray-600' />
                  )}
                </button>
              </div>

              {passwordData.newPassword && (
                <>
                  <div className='mt-2.5 space-y-1.5'>
                    <ValidationIndicator
                      isValid={validation.minLength}
                      text='At least 8 characters'
                    />
                    <ValidationIndicator
                      isValid={validation.hasUppercase}
                      text='At least 1 uppercase letter'
                    />
                    <ValidationIndicator
                      isValid={validation.hasDigit}
                      text='At least 1 digit'
                    />
                    <ValidationIndicator
                      isValid={validation.hasSpecial}
                      text='At least 1 special character'
                    />
                  </div>
                  {passwordData.currentPassword &&
                    !isNewPasswordDifferent &&
                    passwordData.newPassword.length > 0 && (
                      <div className='mt-2 flex items-center gap-2 text-xs text-red-600'>
                        <XCircle className='h-3.5 w-3.5' />
                        <span>Old and new password cannot be the same</span>
                      </div>
                    )}
                </>
              )}
            </div>

            <div>
              <label
                htmlFor='confirmPassword'
                className='block text-xs font-medium text-gray-700 mb-1'
              >
                Confirm New Password
              </label>
              <div className='relative'>
                <Input
                  id='confirmPassword'
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={passwordData.confirmPassword}
                  onChange={(e) =>
                    handlePasswordChange('confirmPassword', e.target.value)
                  }
                  onFocus={(e) => e.target.removeAttribute('readonly')}
                  placeholder='Confirm your new password'
                  icon={<Lock className='h-3.5 w-3.5' />}
                  iconPosition='left'
                  error={passwordErrors.confirmPassword}
                  autoComplete='off'
                  readOnly
                />
                <button
                  type='button'
                  className='absolute inset-y-0 right-0 pr-3 flex items-center z-10'
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className='h-4 w-4 text-gray-400 hover:text-gray-600' />
                  ) : (
                    <Eye className='h-4 w-4 text-gray-400 hover:text-gray-600' />
                  )}
                </button>
              </div>

              {passwordData.confirmPassword && (
                <div className='mt-2'>
                  <div
                    className={`flex items-center space-x-2 text-xs ${
                      doPasswordsMatch ? 'text-green-600' : 'text-red-600'
                    }`}
                  >
                    {doPasswordsMatch ? (
                      <CheckCircle className='h-3.5 w-3.5' />
                    ) : (
                      <XCircle className='h-3.5 w-3.5' />
                    )}
                    <span>
                      {doPasswordsMatch
                        ? 'Passwords match'
                        : 'Passwords do not match'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Save Password Button */}
          <div className='flex justify-end mt-5'>
            <Button
              variant='primary'
              onClick={handleSavePassword}
              loading={isSavingPassword}
              disabled={!canSubmitPassword}
            >
              {isSavingPassword ? 'Updating...' : 'Update Password'}
            </Button>
          </div>
        </Card>
      </div>
    </motion.div>
  )
}

export default ProfileSettings
